import isFunction from 'lodash/isFunction';
import { computed, defineComponent } from 'vue';
import { mapGetters, mapActions, mapState } from 'vuex';
import startCase from 'lodash/startCase';
import { playerFromJson } from '@/models/PlayerModel';
import { PLAYER_REQUEST } from '@/store/actions/player';
import ResultFilter from '@/filters/ResultFilter';
import playerProfileLinksPerRouteProvider from '@/providers/PlayerProfileLinksPerRouteProvider';
import pitcherProfileDataParamsProvider from './providers/pitcherProfileDataParamsProvider';
import batterProfileDataParamsProvider from './providers/batterProfileDataParamsProvider';
import { EReportContext } from '@/enums/reports';
import textCapitalize from '@/filters/text_formatters';
import Base from './profile/Base.vue';

export default defineComponent({
  extends: Base,

  name: 'PlayerProfile',

  metaInfo() {
    let title = `Player Profile ${this.id}`;
    return {
      title,
    };
  },

  components: {
    Honeytrap: () => import(
      /* webpackChunkName: "playerProfiles" */
      '@/modules/player-profiles/views/Honeytrap.vue'),
    DynamicPlayerFilters: () => import(
      /* webpackChunkName: "playerProfiles" */
      '@/modules/player-profiles/filters/DynamicPlayerFilters.vue'),
    StaticPlayerFilters: () => import(
      /* webpackChunkName: "playerProfiles" */
      '@/modules/player-profiles/filters/StaticPlayerFilters.vue'),
    PlayerProfileCard: () => import(
      /* webpackChunkName: "playerProfiles" */
      '@/modules/player-profiles/profile/PlayerProfileCard.vue'),
    SelectedChips: () => import(
      /* webpackChunkName: "playerProfiles" */
      '@/modules/video-library/components/SelectedChips.vue'),
    CareerStats: () => import(
      /* webpackChunkName: "playerProfiles" */
      '@/modules/player-profiles/profile/CareerStats.vue'),
  },
  data() {
    return {
      EReportContext,
      error: null,
      links: playerProfileLinksPerRouteProvider
        .getLinksForRoute('player')
        .filter(({
          header, text, disabled, can
        }) => {
          if (header) return true;
          if (can && isFunction(can)) return can();
          return !disabled;
        }),
      dataFiltersHeight: 160,
    };
  },

  computed: {
    canSeeReports() {
      return this.$can('read', 'PlayerReport');
    },
    canSeeVideo() {
      return this.$can('read', 'Video');
    },
    videoContext() {
      return this.reportType == 'videos';
    },

    routeKey() {
      return `$player-${this.id}`;
    },

    reportRoutekey() {
      return [
        this.$route.name,
        this.$route.params.id,
        this.$route.params.context,
        this.$route.params.reportType
      ].join('-');
    },

    ...mapGetters(['selectedUnitsSystem']),

    currentReport() {
      const { context, reportType } = this.$route.params;
      return this.links.find(
        (link) => link.params && link.params.context === context && link.params.reportType === reportType,
      );
    },

    componentName() {
      return ['Profile', this.view.split('-').map(startCase).join('')].join('');
    },

    reportStyles() {
      return {
        '--filters-h': `${this.dataFiltersHeight}px`,
      };
    },

    showDummyData() {
      return !this.canSeeReports || (this.videoContext && !this.canSeeVideo);
    },

    dialogTitle() {
      return `${textCapitalize(this.context)} Report Preview`;
    },
  },

  created() {
    this.$log.debug('PlayerProfile Page was created.');
  },

  mounted() {
    this.$log.debug('PlayerProfile Page was mounted.');
  },

  methods: {
    onResize() {
      if (this.$refs.dataFilters) {
        this.dataFiltersHeight = this.$refs.dataFilters.clientHeight;
      }
    },
    updateSeasonFrom($event) {
      this.staticFilters.seasonFrom = $event;
    },

    updateSeasonTo($event) {
      this.staticFilters.seasonTo = $event;
    },

    updateSelectedDatesRange($event) {
      this.staticFilters.selectedDatesRange = $event;
    }
  },

  watch: {
    player: {
      handler: 'onResize',
      immediate: true,
    },
  },
});
